import { isLeverId, type BasisPoints, type LeverId, type Scope } from "@helmr/contracts";
import type { BranchRef } from "./auth";
import type { ApiClient } from "./client";

/** `GET /me` — **assumed: followups-tasks-api** (brief G1). Reconcile with the merged route. */
export interface Me {
  personId: string;
  staff: { id: string; name: string; roleId: string; roleName: string; isOwner: boolean };
  business: { id: string; name: string; timezone: string; currency: string; locale: string; country: string };
  /** null in a Helmr support session, which has no home branch (console-support). */
  branch: BranchRef | null;
  branches: BranchRef[];
  /** Whose records and which branches the caller reaches. The levers say what they may do; scope says to whose records. */
  scope: Scope;
  /** The most discount the caller may give, in basis points. It applies only with `billing.discount`. */
  discountCapBp: BasisPoints;
  /** D57: the caller's effective levers; "all" for the owner, who holds every lever implicitly. */
  levers: LeverId[] | "all";
  /** The server's registry version (D57), so a client can tell its bundled registry is out of date. */
  leverRegistryVersion?: number;
  /** Present only in a Helmr support session (console-support): who is looking, read only, until `endsAt`. */
  support?: { sessionId: string; operatorName: string; mode: "read"; endsAt: number };
}

export const getMe = (c: ApiClient) => c.get<Me>("/me");

/** Does the caller hold this lever? The owner holds every lever. Unknown ids are never held. */
export function hasLever(me: Me | null | undefined, lever: LeverId): boolean {
  if (!me || !isLeverId(lever)) return false;
  if (me.levers === "all") return true;
  let held = heldCache.get(me);
  if (!held) heldCache.set(me, (held = new Set(me.levers)));
  return held.has(lever);
}

const heldCache = new WeakMap<Me, ReadonlySet<LeverId>>();

/** What the caller holds, as a lever set: `"all"` for the owner. */
export function heldLevers(me: Me | null | undefined): ReadonlySet<LeverId> | "all" {
  if (!me) return new Set();
  return me.levers === "all" ? "all" : new Set(me.levers);
}
